import { Search } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface FilterOption {
  value: string;
  label: string;
  count?: number;
}

interface DropdownOption {
  value: string;
  label: string;
}

export interface FilterBarProps {
  searchValue: string;
  onSearchChange: (value: string) => void;
  searchPlaceholder?: string;
  searchAriaLabel?: string;
  statusOptions?: FilterOption[];
  selectedStatus?: string;
  onStatusChange?: (value: string) => void;
  sortOptions?: DropdownOption[];
  selectedSort?: string;
  onSortChange?: (value: string) => void;
  sortAriaLabel?: string;
  viewOptions?: DropdownOption[];
  selectedView?: string;
  onViewChange?: (value: string) => void;
  viewAriaLabel?: string;
  className?: string;
}

export default function FilterBar({
  searchValue,
  onSearchChange,
  searchPlaceholder = 'Search...',
  searchAriaLabel = 'Search',
  statusOptions = [],
  selectedStatus,
  onStatusChange,
  sortOptions = [],
  selectedSort,
  onSortChange,
  sortAriaLabel = 'Sort options',
  viewOptions = [],
  selectedView,
  onViewChange,
  viewAriaLabel = 'View options',
  className,
}: FilterBarProps) {
  return (
    <section className={cn('rounded-xl border border-gray-200 bg-white p-4 shadow-sm', className)}>
      <div className="flex flex-col gap-4">
        <label className="relative block">
          <Search className="pointer-events-none absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
          <input
            type="search"
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchAriaLabel}
            className="w-full rounded-lg border border-gray-200 py-2 pl-9 pr-3 text-sm text-gray-900 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
          />
        </label>

        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          {statusOptions.length > 0 ? (
            <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Status filters">
              {statusOptions.map((option) => {
                const isActive = option.value === selectedStatus;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => onStatusChange?.(option.value)}
                    aria-pressed={isActive}
                    className={cn(
                      'rounded-full border px-3 py-1 text-xs font-semibold transition-colors',
                      isActive
                        ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
                        : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
                    )}
                  >
                    {option.label}
                    {typeof option.count === 'number' ? ` (${option.count})` : ''}
                  </button>
                );
              })}
            </div>
          ) : (
            <div aria-hidden="true" />
          )}

          <div className="flex flex-wrap items-center gap-2">
            {sortOptions.length > 0 && (
              <select
                value={selectedSort}
                onChange={(event) => onSortChange?.(event.target.value)}
                aria-label={sortAriaLabel}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
              >
                {sortOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
            {viewOptions.length > 0 && (
              <select
                value={selectedView}
                onChange={(event) => onViewChange?.(event.target.value)}
                aria-label={viewAriaLabel}
                className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
              >
                {viewOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
